import { useEffect, useMemo, useRef, useState } from 'react'
import { findElementsForPath, resolveElementTokens } from '../../config/previewElementRegions'

// Reverse-locate flash lifetimes. Exported so tests derive their wait
// timeouts from the real values instead of re-hardcoding them.
export const FLASH_DURATION_MS = 1800
export const FLASH_MISS_DURATION_MS = 2400

// Keys the widget's window-level listeners consume (see the feed-widget
// surfaces' `window.addEventListener('keydown', …)` handlers): arrow/Enter/
// Space navigation plus Escape, which closes open drawers and story viewers.
const WIDGET_GLOBAL_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Enter',
  ' ',
  'Escape',
])

/**
 * Click-to-edit machinery for a widget preview frame ("Edit elements").
 *
 * With inspect mode on, widget interactivity inside the frame is suppressed,
 * hovering outlines the styleable region under the cursor
 * (`data-inspect-hover`), and clicking marks it (`data-inspect-selected`),
 * resolves it to the token paths in `activePaths` and hands them to
 * `onTokenSelect({ label, paths })` (null when nothing editable was hit or
 * inspect mode ends). Independently of inspect mode, a new `flashRequest`
 * (`{ path, nonce }`) flashes the elements that path styles
 * (`data-inspect-flash`), or reports a miss through `flashMiss`.
 *
 * `resetKey` identifies what the frame shows; when it changes the widget tree
 * remounts, so markers, flashes and the status are dropped.
 *
 * Spread `frameProps` on the frame element (the one `frameRef` points at) and
 * add the canvas's `frameInspecting` class while `inspectMode` is on.
 */
export default function usePreviewInspect({
  frameRef,
  activePaths,
  onTokenSelect,
  flashRequest = null,
  resetKey,
}) {
  const [inspectMode, setInspectMode] = useState(false)
  const [inspectStatus, setInspectStatus] = useState(null) // { kind: 'selected'|'none', label? }
  const [flashMiss, setFlashMiss] = useState(false)
  const hoverElRef = useRef(null)
  const selectedElRef = useRef(null)
  const flashElsRef = useRef([])
  const flashTimerRef = useRef(null)

  const activePathSet = useMemo(
    () => (activePaths instanceof Set ? activePaths : new Set(activePaths || [])),
    [activePaths]
  )

  const setMarker = (ref, attribute, element) => {
    if (ref.current === element) return
    if (ref.current?.isConnected) ref.current.removeAttribute(attribute)
    ref.current = element
    if (element) element.setAttribute(attribute, 'true')
  }

  const clearMarkers = () => {
    setMarker(hoverElRef, 'data-inspect-hover', null)
    setMarker(selectedElRef, 'data-inspect-selected', null)
  }

  const clearFlash = () => {
    if (flashTimerRef.current) {
      clearTimeout(flashTimerRef.current)
      flashTimerRef.current = null
    }
    for (const element of flashElsRef.current) {
      if (element.isConnected) element.removeAttribute('data-inspect-flash')
    }
    flashElsRef.current = []
  }

  const toggleInspectMode = () => {
    clearMarkers()
    setInspectStatus(null)
    if (inspectMode) onTokenSelect?.(null)
    setInspectMode(!inspectMode)
  }

  // A new resetKey remounts the widget tree; drop stale markers.
  useEffect(() => {
    clearMarkers()
    clearFlash()
    setInspectStatus(null)
    setFlashMiss(false)
    onTokenSelect?.(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])

  // Reverse locate: flash the preview elements a token path styles.
  useEffect(() => {
    if (!flashRequest?.nonce || !flashRequest.path) return undefined
    clearFlash()
    // Locate switches panes on small screens. Move focus out of the now
    // hidden inspector so keyboard navigation continues in the preview.
    frameRef.current?.focus({ preventScroll: true })
    const found = findElementsForPath(flashRequest.path, frameRef.current)
    if (!found) {
      setFlashMiss(true)
      const missTimer = setTimeout(() => setFlashMiss(false), FLASH_MISS_DURATION_MS)
      return () => clearTimeout(missTimer)
    }
    setFlashMiss(false)
    for (const element of found.elements) {
      element.setAttribute('data-inspect-flash', 'true')
    }
    flashElsRef.current = found.elements
    const [first] = found.elements
    if (typeof first.scrollIntoView === 'function') {
      first.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' })
    }
    flashTimerRef.current = setTimeout(clearFlash, FLASH_DURATION_MS)
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flashRequest?.nonce])

  // Drop timers and stray attributes on unmount.
  useEffect(() => () => clearFlash(), [])

  // Widget surfaces attach window-level keydown handlers (hero/carousel
  // arrows, player-deck Enter/Space, story navigation, Escape-to-close on
  // open drawers/viewers) that fire no matter where focus is. While edit
  // mode is on, stop those keys at the document root — Studio's own React
  // handlers attach below document and have already run, and stopPropagation
  // leaves default actions (caret movement, typing, scrolling) intact.
  // Studio overlays are unaffected: OverlayFoundation listens on window in
  // the CAPTURE phase, which runs before this bubble-phase stop.
  useEffect(() => {
    if (!inspectMode) return undefined
    const silenceWidgetGlobalKeys = (event) => {
      if (WIDGET_GLOBAL_KEYS.has(event.key)) event.stopPropagation()
    }
    document.addEventListener('keydown', silenceWidgetGlobalKeys)
    return () => document.removeEventListener('keydown', silenceWidgetGlobalKeys)
  }, [inspectMode])

  const suppressEvent = (event) => {
    if (!inspectMode) return
    event.preventDefault()
    event.stopPropagation()
  }

  // Keyboard counterpart of the pointer suppressors below: stopPropagation
  // (never preventDefault) kills the widget's own key handlers — both React
  // onKeyDown on frame descendants and the window-level listeners, which run
  // last — while preserving Tab focus movement, scroll keys, and native
  // Enter/Space click synthesis on buttons (that click lands in
  // handleClickCapture, which owns clicks in edit mode).
  const suppressKeyEvent = (event) => {
    if (!inspectMode) return
    event.stopPropagation()
  }

  const handleClickCapture = (event) => {
    if (!inspectMode) return
    event.preventDefault()
    event.stopPropagation()
    const resolved = resolveElementTokens(event.target, frameRef.current, activePathSet)
    if (resolved) {
      setMarker(selectedElRef, 'data-inspect-selected', resolved.element)
      setInspectStatus({ kind: 'selected', label: resolved.label })
      onTokenSelect?.({ label: resolved.label, paths: resolved.paths })
    } else {
      setMarker(selectedElRef, 'data-inspect-selected', null)
      setInspectStatus({ kind: 'none' })
      onTokenSelect?.(null)
    }
  }

  const handleMouseOverCapture = (event) => {
    if (!inspectMode) return
    const resolved = resolveElementTokens(event.target, frameRef.current, activePathSet)
    setMarker(hoverElRef, 'data-inspect-hover', resolved?.element || null)
  }

  const handleMouseLeave = () => {
    if (!inspectMode) return
    setMarker(hoverElRef, 'data-inspect-hover', null)
  }

  const frameProps = {
    tabIndex: -1,
    'data-inspect-mode': inspectMode ? 'true' : undefined,
    onClickCapture: handleClickCapture,
    onMouseDownCapture: suppressEvent,
    onPointerDownCapture: suppressEvent,
    onKeyDownCapture: suppressKeyEvent,
    onKeyUpCapture: suppressKeyEvent,
    onMouseOverCapture: handleMouseOverCapture,
    onMouseLeave: handleMouseLeave,
  }

  return { inspectMode, toggleInspectMode, inspectStatus, flashMiss, frameProps }
}
